import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useLicitacoesFeed } from '@/hooks/useLicitacoes';
import { useAdicionarAoPipeline } from '@/hooks/useGestaoLicitacoes';
import type { EmpresaListItem } from '@/types/empresa';
import type { PipelineEntradaGeral } from '@/types/gestaoLicitacoes';

interface AdicionarAoPipelineModalProps {
  isOpen: boolean;
  empresas: EmpresaListItem[];
  defaultEmpresaId?: number;
  pipeline: PipelineEntradaGeral[];
  onClose: () => void;
}

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

function AdicionarAoPipelineModal({ isOpen, empresas, defaultEmpresaId, pipeline, onClose }: AdicionarAoPipelineModalProps) {
  const [empresaId, setEmpresaId] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) setEmpresaId(defaultEmpresaId ? String(defaultEmpresaId) : '');
  }, [isOpen, defaultEmpresaId]);

  const empresaIdNum = parseInt(empresaId, 10) || undefined;

  const { data: feed, isLoading } = useLicitacoesFeed({ search: search || undefined, limit: 20 });
  const adicionar = useAdicionarAoPipeline(empresaIdNum);

  const licitacoesNoPipeline = new Set(
    pipeline.filter((entrada) => entrada.empresaId === empresaIdNum).map((entrada) => entrada.licitacaoId)
  );
  const resultados = feed?.licitacoes || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adicionar à gestão">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="gestao-adicionar-empresa">
            Empresa (cliente)
          </label>
          <select
            id="gestao-adicionar-empresa"
            className={selectClassName}
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
          >
            <option value="">Selecione uma empresa</option>
            {empresas.map((empresa) => (
              <option key={empresa.id} value={empresa.id}>{empresa.razao_social}</option>
            ))}
          </select>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
          <Input
            placeholder="Buscar por objeto, órgão, número do edital..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {adicionar.isError && (
          <p className="text-sm text-ds-danger">Não foi possível adicionar a licitação. Tente novamente.</p>
        )}

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {!empresaIdNum ? (
            <p className="text-sm text-ds-text-secondary text-center py-6">Selecione uma empresa para adicionar licitações.</p>
          ) : isLoading ? (
            <p className="text-sm text-ds-text-secondary text-center py-6">Buscando...</p>
          ) : resultados.length === 0 ? (
            <p className="text-sm text-ds-text-secondary text-center py-6">Nenhuma licitação encontrada.</p>
          ) : (
            resultados.map((licitacao) => {
              const jaAdicionada = licitacoesNoPipeline.has(licitacao.id);
              return (
                <div
                  key={licitacao.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-ds-border bg-ds-surface2 p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ds-text-primary truncate">{licitacao.objeto}</p>
                    <p className="text-xs text-ds-text-secondary truncate">
                      {[licitacao.orgao, licitacao.uf, licitacao.modalidade].filter(Boolean).join(' · ')}
                    </p>
                    {licitacao.valor_estimado != null && (
                      <p className="text-xs font-semibold text-ds-success mt-0.5">
                        {formatCurrency(Number(licitacao.valor_estimado))}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={jaAdicionada || adicionar.isPending}
                    onClick={() => adicionar.mutate(licitacao.id)}
                  >
                    {jaAdicionada ? 'Já adicionada' : 'Adicionar'}
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

export { AdicionarAoPipelineModal };
